import React, { FC, useMemo, useState } from 'react';
import { ModeSwitch } from './ModeSwitch';
import {
    AppBar,
    Box,
    Button,
    CssBaseline,
    ThemeProvider,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
    IconButton,
    Menu,
    MenuItem
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import MenuIcon from '@mui/icons-material/Menu';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedYear } from '../../features/yearSlice';
import { MobileMenu } from './MobileMenu';
import { getTheme } from '../../utils/getTheme';
import { useNavigate } from 'react-router-dom';

interface Props {
    children: React.ReactNode;
}

export const Header: FC<Props> = ({ children }) => {
    const [toggleDarkMode, setToggleDarkMode] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const theme = useMemo(() => getTheme(toggleDarkMode), [toggleDarkMode]);
    const themeBreakpoint = useTheme();
    const isMobile = useMediaQuery(themeBreakpoint.breakpoints.down('md'));

    const onToggleDarkTheme = () => {
        setToggleDarkMode(!toggleDarkMode);
    };

    const selectedYear: number = useSelector((state: any) => state.years.selectedYear);
    const years: number[] = useSelector((state: any) => state.years.years);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const onYearSelect = (year: number) => {
        dispatch(setSelectedYear(year));
        navigate('/');
        handleMenuClose();
    };

    return (
        <>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AppBar position="static" className="header">
                    <Toolbar sx={{ position: 'relative' }}>
                        
                        {/* LEVÁ ČÁST: Ikonky pro kontakt */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton 
                                color="inherit" 
                                onClick={() => navigate('/contact')} 
                                sx={{ mr: 1 }}
                            >
                                <EmailIcon />
                                <PhoneIcon sx={{ ml: 0.5, fontSize: '1.2rem' }} />
                            </IconButton>
                        </Box>

                        {/* STŘED: Nadpis */}
                        <Typography
                            variant="h6"
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer' // Přidán kurzor, pokud byste chtěli časem udělat odkaz i z nadpisu
                            }}
                            onClick={() => navigate('/')} // Nadpis také vrací na hlavní stranu (běžná praxe)
                        >
                            Ava Results 🚴
                        </Typography>

                        {/* PRAVÁ ČÁST: Desktop Menu (Roky + Dark Mode) */}
                        {!isMobile && (
                            <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                                
                                {/* 1. Aktuálně vybraný rok nově vrací na hlavní stránku */}
                                <Button 
                                    color="inherit" 
                                    className="active" 
                                    onClick={() => navigate('/')}
                                >
                                    {selectedYear}
                                </Button>

                                {/* 2. Ikonka menu pro ostatní roky */}
                                {years.length > 1 && (
                                    <>
                                        <IconButton color="inherit" onClick={handleMenuOpen}>
                                            <MenuIcon />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleMenuClose}
                                        >
                                            {years
                                                .filter((year) => year !== selectedYear)
                                                .map((year) => (
                                                    <MenuItem 
                                                        key={year} 
                                                        onClick={() => onYearSelect(year)}
                                                    >
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                        </Menu>
                                    </>
                                )}

                                {/* 3. Přepínač den/noc */}
                                <ModeSwitch onToggleDarkMode={onToggleDarkTheme} toggleDarkMode={toggleDarkMode} />
                            </Box>
                        )}

                        {/* Mobile Menu Button */}
                        {isMobile && (
                            <MobileMenu onToggleDarkTheme={onToggleDarkTheme} toggleDarkMode={toggleDarkMode} />
                        )}
                    </Toolbar>
                </AppBar>
                {children}
            </ThemeProvider>
        </>
    );
};